import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#setupSkuThumbnailGallery();
    this.#setupInstantVariantGallery();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  #setupVariantImageSwatches() {
    if (this.dataset.onnutoMediaGroups !== 'sku-v2') return;

    const target = this.closest('.shopify-section, dialog') || document;
    const { signal } = this.#controller;
    const render = () => this.#renderVariantImageSwatches();

    this.#installVariantSwatchStyles();
    this.#runVariantSwatchRenderLoop(render, target);

    target.addEventListener(
      'change',
      (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement)) return;
        if (!input.matches('input[data-option-name="Color"], input[data-option-name="color"], input[data-onnuto-sku]')) return;

        render();
      },
      { signal }
    );
  }

  #runVariantSwatchRenderLoop(render, target) {
    render();
    requestAnimationFrame(render);

    let attempts = 0;
    const maxAttempts = 6;
    const interval = window.setInterval(() => {
      attempts += 1;
      render();
      if (attempts >= maxAttempts || this.#controller.signal.aborted) {
        window.clearInterval(interval);
      }
    }, 250);

    this.#controller.signal.addEventListener('abort', () => window.clearInterval(interval), { once: true });

    let pendingRender = false;
    const scheduleRender = () => {
      if (pendingRender) return;
      pendingRender = true;
      requestAnimationFrame(() => {
        pendingRender = false;
        render();
      });
    };

    const observer = new MutationObserver((mutations) => {
      const hasVariantPickerChange = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).concat(Array.from(mutation.removedNodes)).some((node) => {
          if (!(node instanceof Element)) return false;
          return node.matches('variant-picker, .variant-option, label') || Boolean(node.querySelector?.('variant-picker, .variant-option, label'));
        })
      );
      if (hasVariantPickerChange) scheduleRender();
    });
    observer.observe(target, {
      childList: true,
      subtree: true,
    });
    this.#controller.signal.addEventListener('abort', () => observer.disconnect(), { once: true });

    window.addEventListener('load', render, { once: true, signal: this.#controller.signal });
    window.addEventListener('pageshow', render, { signal: this.#controller.signal });
  }

  #renderVariantImageSwatches() {
    const target = this.closest('.shopify-section, dialog') || document;
    const picker = target.querySelector('variant-picker[data-product-handle]');
    if (!(picker instanceof HTMLElement)) return false;

    const styleCode = this.#styleCodeFromHandle(picker.dataset.productHandle || '');
    if (!styleCode) return false;

    const inputs = picker.querySelectorAll('input[data-option-name="Color"], input[data-option-name="color"]');
    let renderedCount = 0;

    for (const input of inputs) {
      if (!(input instanceof HTMLInputElement)) continue;

      const sku = input.dataset.onnutoSku || this.#skuFromColorValue(styleCode, input.value);
      if (!sku) continue;

      const image = this.#imageForSku(sku);
      if (!image) continue;

      input.dataset.onnutoSku = sku;
      const label = input.closest('label');
      if (!(label instanceof HTMLElement)) continue;

      label.classList.add('onnuto-image-swatch-label');
      label.classList.toggle('is-selected', input.checked);
      label.style.setProperty('width', '40px', 'important');
      label.style.setProperty('min-width', '40px', 'important');
      label.style.setProperty('height', '40px', 'important');
      label.style.setProperty('min-height', '40px', 'important');
      label.style.setProperty('padding', '0', 'important');
      label.style.setProperty('border-radius', '999px', 'important');
      label.style.setProperty('overflow', 'visible', 'important');
      label.style.setProperty('background', '#fff', 'important');

      this.#replaceLabelWithImageSwatch(label, input, image, input.value);
      renderedCount += 1;
    }

    return renderedCount > 0;
  }

  #styleCodeFromHandle(handle) {
    const match = handle.match(/[a-z]{3}\d{3,}/i);
    return match ? match[0].toUpperCase() : '';
  }

  #skuFromColorValue(styleCode, value) {
    const match = value.match(/\d+/);
    if (!match) return '';

    return `${styleCode}-${match[0].padStart(2, '0')}`;
  }

  #imageForSku(sku) {
    return Array.from(this.querySelectorAll('img')).find((image) => {
      if (!(image instanceof HTMLImageElement)) return false;
      const src = decodeURIComponent(image.currentSrc || image.src || '');
      return image.alt.includes(sku) || src.includes(sku);
    });
  }

  #replaceLabelWithImageSwatch(label, input, sourceImage, value) {
    if (!label.querySelector('.onnuto-image-swatch')) {
      for (const node of Array.from(label.childNodes)) {
        if (node !== input) node.remove();
      }

      const swatch = document.createElement('span');
      swatch.className = 'onnuto-image-swatch';
      swatch.title = value;

      const image = document.createElement('img');
      image.src = sourceImage.currentSrc || sourceImage.src;
      image.alt = value;
      image.loading = 'lazy';

      const hiddenText = document.createElement('span');
      hiddenText.className = 'visually-hidden';
      hiddenText.textContent = value;

      swatch.appendChild(image);
      label.appendChild(swatch);
      label.appendChild(hiddenText);
    }

    const swatch = label.querySelector('.onnuto-image-swatch');
    if (swatch instanceof HTMLElement) {
      swatch.style.setProperty('width', '32px', 'important');
      swatch.style.setProperty('height', '32px', 'important');
      swatch.style.setProperty('display', 'block', 'important');
      swatch.style.setProperty('border-radius', '999px', 'important');
      swatch.style.setProperty('overflow', 'hidden', 'important');
    }

    const image = label.querySelector('.onnuto-image-swatch img');
    if (image instanceof HTMLImageElement) {
      image.style.setProperty('width', '100%', 'important');
      image.style.setProperty('height', '100%', 'important');
      image.style.setProperty('display', 'block', 'important');
      image.style.setProperty('object-fit', 'cover', 'important');
    }
  }

  #installVariantSwatchStyles() {
    if (document.getElementById('onnuto-variant-image-swatch-runtime-styles')) return;

    const style = document.createElement('style');
    style.id = 'onnuto-variant-image-swatch-runtime-styles';
    style.textContent = `
      .variant-picker .onnuto-image-swatch-label {
        width: 40px !important;
        min-width: 40px !important;
        height: 40px !important;
        min-height: 40px !important;
        padding: 0 !important;
        border-radius: 999px !important;
        border: 1px solid #d8d2ca !important;
        background: #fff !important;
      }

      .variant-picker .onnuto-image-swatch-label.is-selected,
      .variant-picker .onnuto-image-swatch-label:has(:checked) {
        border-color: #202020 !important;
        box-shadow: 0 0 0 2px #fff, 0 0 0 3px #202020 !important;
      }

      .variant-picker .onnuto-image-swatch-label::before,
      .variant-picker .onnuto-image-swatch-label::after,
      .variant-picker .onnuto-image-swatch-label .variant-option__button-label__pill,
      .variant-picker .onnuto-image-swatch-label .variant-option__button-label__text {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  #setupInstantVariantGallery() {
    if (this.dataset.onnutoMediaGroups !== 'sku-v2') return;

    const target = this.closest('.shopify-section, dialog');
    const { signal } = this.#controller;

    target?.addEventListener(
      'change',
      (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || !input.matches('input[data-onnuto-sku]')) return;

        const optionValue = input.closest('.variant-option')?.querySelector('.variant-option__swatch-value');
        if (optionValue) optionValue.textContent = input.value;

        this.#renderOnnutoSku(input.dataset.onnutoSku);
      },
      { signal }
    );
  }

  #selectedOnnutoSku() {
    const target = this.closest('.shopify-section, dialog') || document;
    const checked = target.querySelector('input[data-onnuto-sku]:checked');
    if (checked instanceof HTMLInputElement && checked.dataset.onnutoSku) return checked.dataset.onnutoSku;

    return this.dataset.onnutoSku;
  }

  #renderOnnutoSku(sku) {
    return this.#setupSkuThumbnailGallery(sku);
  }

  #setupSkuThumbnailGallery(targetSku) {
    const firstSkuImage = this.querySelector('img[alt*="-"]');
    if (!(firstSkuImage instanceof HTMLImageElement)) return false;

    const skuMatch = firstSkuImage.alt.match(/[A-Z0-9]+-\d+/i);
    const sku = targetSku || this.#selectedOnnutoSku() || (skuMatch ? skuMatch[0] : '');
    if (!sku) return false;
    if (this.dataset.onnutoSku === sku && this.querySelector(':scope > .onnuto-sku-thumbs')) return true;

    this.querySelector(':scope > .onnuto-sku-thumbs')?.remove();
    this.dataset.onnutoSku = sku;

    this.#installSkuThumbnailStyles();

    const allSkuSlides = Array.from(this.querySelectorAll('slideshow-slide')).filter((slide) => {
      const image = slide.querySelector('img');
      return image instanceof HTMLImageElement && /[A-Z0-9]+-\d+/i.test(image.alt);
    });
    const slides = allSkuSlides.filter((slide) => slide.querySelector(`img[alt*="${sku}"]`));
    if (slides.length <= 1) return false;

    this.classList.add('onnuto-sku-gallery');
    this.querySelector('slideshow-slides')?.style.setProperty('display', 'block', 'important');

    const thumbs = document.createElement('div');
    thumbs.className = 'onnuto-sku-thumbs';
    thumbs.setAttribute('aria-label', 'Product images');

    const setActive = (activeIndex) => {
      allSkuSlides.forEach((slide) => {
        slide.dataset.onnutoActive = 'false';
        slide.setAttribute('aria-hidden', 'true');
        slide.style.setProperty('display', 'none', 'important');
      });

      slides.forEach((slide, index) => {
        const isActive = index === activeIndex;
        slide.dataset.onnutoActive = isActive ? 'true' : 'false';
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        slide.style.setProperty('display', isActive ? 'block' : 'none', 'important');
        slide.style.setProperty('width', '100%', 'important');
        slide.style.setProperty('max-width', 'none', 'important');
        slide.style.setProperty('min-width', '0', 'important');
      });

      for (const button of thumbs.querySelectorAll('button')) {
        button.setAttribute('aria-current', button.dataset.index === String(activeIndex) ? 'true' : 'false');
      }
    };

    slides.forEach((slide, index) => {
      const image = slide.querySelector('img');
      if (!(image instanceof HTMLImageElement)) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'onnuto-sku-thumb';
      button.dataset.index = String(index);
      button.setAttribute('aria-label', image.alt || `Product image ${index + 1}`);

      const thumbImage = document.createElement('img');
      thumbImage.src = image.currentSrc || image.src;
      thumbImage.alt = image.alt || '';
      thumbImage.loading = 'lazy';
      thumbImage.style.setProperty('width', '100%', 'important');
      thumbImage.style.setProperty('height', '100%', 'important');
      thumbImage.style.setProperty('object-fit', 'cover', 'important');

      button.appendChild(thumbImage);
      button.addEventListener('click', () => setActive(index));
      thumbs.appendChild(button);
    });

    this.insertBefore(thumbs, this.querySelector('slideshow-component'));
    setActive(0);
    return true;
  }

  #installSkuThumbnailStyles() {
    if (document.getElementById('onnuto-sku-thumbnail-gallery-styles')) return;

    const style = document.createElement('style');
    style.id = 'onnuto-sku-thumbnail-gallery-styles';
    style.textContent = `
      media-gallery.onnuto-sku-gallery {
        --image-gap: 10px;
        align-self: start;
        display: grid;
        grid-template-columns: 64px minmax(0, 1fr);
        gap: var(--image-gap);
      }

      media-gallery.onnuto-sku-gallery slideshow-component {
        display: block !important;
        grid-column: 2;
        grid-row: 1;
        min-width: 0;
      }

      media-gallery.onnuto-sku-gallery slideshow-container,
      media-gallery.onnuto-sku-gallery slideshow-slides {
        display: block !important;
        min-width: 0;
        overflow: hidden !important;
        scroll-snap-type: none !important;
        transform: none !important;
      }

      media-gallery.onnuto-sku-gallery .media-gallery__grid,
      media-gallery.onnuto-sku-gallery slideshow-controls,
      media-gallery.onnuto-sku-gallery slideshow-arrows {
        display: none !important;
      }

      media-gallery.onnuto-sku-gallery slideshow-slide {
        display: none !important;
        position: relative !important;
        width: auto !important;
        min-width: 0 !important;
        max-width: none !important;
        visibility: visible !important;
        opacity: 1 !important;
        transform: none !important;
        pointer-events: auto !important;
        border-radius: 0 !important;
        background: #fff;
        overflow: hidden;
      }

      media-gallery.onnuto-sku-gallery slideshow-slide[data-onnuto-active="true"] {
        display: block !important;
      }

      media-gallery.onnuto-sku-gallery .product-media,
      media-gallery.onnuto-sku-gallery .product-media-container {
        background: #fff;
        border-radius: 0 !important;
      }

      media-gallery.onnuto-sku-gallery .product-media {
        aspect-ratio: 1 / 1;
      }

      media-gallery.onnuto-sku-gallery .product-media__image {
        object-fit: contain !important;
        padding: 0;
      }

      .onnuto-sku-thumbs {
        display: flex;
        flex-direction: column;
        gap: 8px;
        grid-column: 1;
        grid-row: 1;
        max-height: calc(100vh - 130px);
        overflow-y: auto;
        scrollbar-width: thin;
      }

      .onnuto-sku-thumb {
        width: 64px;
        height: 64px;
        padding: 0;
        border: 1px solid transparent;
        border-radius: 8px;
        background: #fff;
        cursor: pointer;
        overflow: hidden;
      }

      .onnuto-sku-thumb[aria-current="true"] {
        border-color: #202020;
      }

      .onnuto-sku-thumb img {
        width: 100%;
        height: 100%;
        display: block;
        object-fit: cover;
      }

      @media screen and (max-width: 749px) {
        media-gallery.onnuto-sku-gallery {
          --image-gap: 0px;
          display: flex;
          flex-direction: column;
        }

        .onnuto-sku-thumbs {
          order: 2;
          flex-direction: row;
          gap: 8px;
          max-height: none;
          overflow-x: auto;
          padding-top: 8px;
        }

        .onnuto-sku-thumb {
          width: 58px;
          height: 58px;
          flex: 0 0 58px;
        }
      }
    `;

    document.head.appendChild(style);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    if (this.dataset.onnutoMediaGroups === 'sku-v2' && this.#renderOnnutoSku(this.#selectedOnnutoSku())) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
